import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Input,
  Label,
  Table,
  Badge,
  FormGroup,
} from 'reactstrap';

import { plans } from '../../Config/pitti-plans';

const TRIAL_PLAN_ID = plans[0]?.id; // Trial plan is the default

const BulkPlanSelectionModal = ({ open, onClose, accounts, loading, onSubmit }) => {
  const [userPlans, setUserPlans] = useState({});
  const [userCRM, setUserCRM] = useState({});
  const [commonPlan, setCommonPlan] = useState('');
  const [commonCRM, setCommonCRM] = useState(false);

  useEffect(() => {
    if (open && accounts?.length > 0) {
      const initialPlans = {};
      const initialCRM = {};
      accounts.forEach((_, index) => {
        initialPlans[index] = TRIAL_PLAN_ID;
        initialCRM[index] = false;
      });
      setUserPlans(initialPlans);
      setUserCRM(initialCRM);
      setCommonPlan('');
      setCommonCRM(false);
    }
  }, [open, accounts]);

  const handleCommonPlanChange = (planId) => {
    setCommonPlan(planId);
    if (planId) {
      const updated = {};
      accounts.forEach((_, index) => {
        updated[index] = Number(planId);
      });
      setUserPlans(updated);
    }
  };

  const handleCommonCRMChange = (checked) => {
    setCommonCRM(checked);
    const updated = {};
    accounts.forEach((_, index) => {
      updated[index] = checked;
    });
    setUserCRM(updated);
  };

  const handleUserPlanChange = (index, planId) => {
    setUserPlans((prev) => ({
      ...prev,
      [index]: Number(planId),
    }));
    setCommonPlan('');
  };

  const handleUserCRMChange = (index, checked) => {
    setUserCRM((prev) => ({
      ...prev,
      [index]: checked,
    }));
    // Check if all are now the same; if not, leave commonCRM as-is visually
    const allSame = accounts.every((_, i) => (i === index ? checked : userCRM[i]) === checked);
    if (!allSame) {
      setCommonCRM(false);
    }
  };

  const handleConfirm = () => {
    const accountsWithPlans = accounts.map((account, index) => ({
      ...account,
      pitti_plan_id: userPlans[index] || TRIAL_PLAN_ID,
      enable_crm: userCRM[index] || false,
    }));
    onSubmit(accountsWithPlans);
  };

  return (
    <Modal isOpen={open} toggle={loading ? undefined : onClose} centered size="xl">
      <ModalHeader toggle={loading ? undefined : onClose}>
        <span style={{ fontSize: '18px', fontWeight: '700' }}>
          Assign Plans to Accounts
        </span>
      </ModalHeader>
      <ModalBody>
        {/* Common controls */}
        <div
          className="d-flex align-items-center gap-4 mb-4 p-3 flex-wrap"
          style={{ backgroundColor: '#f0f7ff', borderRadius: '8px', border: '1px solid #d0e3ff' }}
        >
          <FormGroup className="mb-0 d-flex align-items-center gap-2">
            <Label
              className="mb-0 flex-shrink-0"
              style={{ fontWeight: '600', fontSize: '14px' }}
            >
              Apply plan to all:
            </Label>
            <Input
              type="select"
              value={commonPlan}
              onChange={(e) => handleCommonPlanChange(e.target.value)}
              style={{ fontSize: '14px', maxWidth: '250px' }}
            >
              <option value="">-- Select a plan --</option>
              {plans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name} ({plan.currency}{plan.amount}/{plan.type})
                </option>
              ))}
            </Input>
          </FormGroup>
          <FormGroup className="mb-0 d-flex align-items-center gap-2">
            <div className="form-check form-switch mb-0">
              <Input
                type="switch"
                id="commonCRM"
                checked={commonCRM}
                onChange={(e) => handleCommonCRMChange(e.target.checked)}
                style={{ cursor: 'pointer', width: '36px', height: '18px' }}
              />
              <Label
                for="commonCRM"
                className="form-check-label mb-0"
                style={{ fontWeight: '600', fontSize: '14px', cursor: 'pointer', marginLeft: '6px' }}
              >
                Enable CRM for all
              </Label>
            </div>
          </FormGroup>
          <span className="text-muted ms-auto" style={{ fontSize: '12px', flexShrink: 0 }}>
            {accounts?.length || 0} account(s)
          </span>
        </div>

        {/* Users table */}
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <Table bordered hover responsive size="sm">
            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
              <tr>
                <th style={{ fontSize: '13px', fontWeight: '600', width: '40px' }}>#</th>
                <th style={{ fontSize: '13px', fontWeight: '600' }}>Name</th>
                <th style={{ fontSize: '13px', fontWeight: '600' }}>Email</th>
                <th style={{ fontSize: '13px', fontWeight: '600' }}>Type</th>
                <th style={{ fontSize: '13px', fontWeight: '600' }}>Pitti ID</th>
                <th style={{ fontSize: '13px', fontWeight: '600', minWidth: '200px' }}>Plan</th>
                <th style={{ fontSize: '13px', fontWeight: '600', width: '70px', textAlign: 'center' }}>CRM</th>
              </tr>
            </thead>
            <tbody>
              {accounts?.map((account, index) => (
                <tr key={index}>
                  <td style={{ fontSize: '13px', verticalAlign: 'middle' }}>{index + 1}</td>
                  <td style={{ fontSize: '13px', verticalAlign: 'middle' }}>
                    {account.personal_name || '--'}
                  </td>
                  <td style={{ fontSize: '13px', verticalAlign: 'middle' }}>
                    {account.email || '--'}
                  </td>
                  <td style={{ fontSize: '13px', verticalAlign: 'middle' }}>
                    <Badge
                      color={account.type === 'brand' ? 'light-primary' : 'light-success'}
                      style={{ fontSize: '11px', textTransform: 'capitalize' }}
                    >
                      {account.type || '--'}
                    </Badge>
                  </td>
                  <td style={{ fontSize: '13px', verticalAlign: 'middle' }}>
                    {account.pitti_id || '--'}
                  </td>
                  <td style={{ verticalAlign: 'middle' }}>
                    <Input
                      type="select"
                      bsSize="sm"
                      value={userPlans[index] || TRIAL_PLAN_ID}
                      onChange={(e) => handleUserPlanChange(index, e.target.value)}
                      style={{ fontSize: '13px' }}
                    >
                      {plans.map((plan) => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name} ({plan.currency}{plan.amount})
                        </option>
                      ))}
                    </Input>
                  </td>
                  <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                    <div className="form-check form-switch d-flex justify-content-center mb-0">
                      <Input
                        type="switch"
                        checked={userCRM[index] || false}
                        onChange={(e) => handleUserCRMChange(index, e.target.checked)}
                        style={{ cursor: 'pointer', width: '36px', height: '18px' }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {/* Plan legend */}
        <div className="mt-3 p-2" style={{ backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#666', marginBottom: '4px' }}>
            Available Plans:
          </div>
          <div className="d-flex flex-wrap gap-3">
            {plans.map((plan) => (
              <div key={plan.id} style={{ fontSize: '12px', color: '#555' }}>
                <strong>{plan.name}</strong>: {plan.currency}{plan.amount}/{plan.type}
                {' '}(Seats: {plan.seats}, Search: {plan.max_search_credits}, Match: {plan.max_match_credits})
              </div>
            ))}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleConfirm} disabled={loading}>
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Sending Invites...
            </>
          ) : (
            `Confirm & Send ${accounts?.length || 0} Invite(s)`
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BulkPlanSelectionModal;
